import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  styled,
  LinearProgress
} from '@mui/material';
import { VideoLibrary as VideoLibraryIcon } from '@mui/icons-material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../context/AuthContext';
import { uploadVideo } from '../services/storageService';
import { fetchProfiles as obtenerPerfiles } from '../utils/api';

const UploadContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: '12px',
}));

const FileInput = styled('input')({
  display: 'none',
});

const UploadButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2, 4),
  fontSize: '1.1rem',
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  justifyContent: 'center',
}));

const VideoPreview = styled('video')(({ theme }) => ({
  height: '200px',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
}));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const UploadVideo = () => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();
  const query = useQuery();
  
  // Obtener el perfil de la URL si existe
  useEffect(() => {
    const profileId = query.get('profile');
    if (profileId) {
      setSelectedProfile(profileId);
    }
  }, [query.get('profile')]); // Solo depende del parámetro 'profile' en la URL
  
  // Cargar perfiles solo una vez al montar el componente
  useEffect(() => {
    const cargarPerfiles = async () => {
      try {
        // Via la API, no leyendo Supabase directo desde el navegador: asi las
        // portadas vienen con la url reescrita al bucket actual, en lugar de
        // apuntar al dominio viejo que ya no resuelve.
        const perfiles = await obtenerPerfiles();
        setProfiles(perfiles);
      } catch (err) {
        console.error('Error fetching profiles:', err);
      }
    };

    cargarPerfiles();
  }, []);  // Dependencia vacía para que solo se ejecute una vez al montar el componente
  
  // Función para generar una miniatura a partir de un video
  const generateThumbnail = (videoFile) => {
    return new Promise((resolve) => {
      // Crear elementos temporales para generar la miniatura
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Crear URL para el video
      const videoURL = URL.createObjectURL(videoFile);
      
      // Configurar el video
      video.setAttribute('src', videoURL);
      video.load();
      
      // Cuando los metadatos estén cargados, configurar el tamaño del canvas
      video.onloadedmetadata = () => {
        // Determinar si es un video vertical u horizontal
        const isVertical = video.videoHeight > video.videoWidth;
        
        let width, height;
        
        if (isVertical) {
          // Para videos verticales (formato 9:16 o similar)
          height = 480; // Altura máxima
          width = Math.floor((video.videoWidth / video.videoHeight) * height);
        } else {
          // Para videos horizontales (formato 16:9 o similar)
          width = 640; // Anchura máxima
          height = Math.floor((video.videoHeight / video.videoWidth) * width);
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Avanzar un poco en el video para evitar pantallas negras
        video.currentTime = 1.0;
      };
      
      // Cuando el video avance, capturar el fotograma
      video.onseeked = () => {
        // Dibujar el fotograma en el canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convertir el canvas a una URL de datos
        const thumbnailURL = canvas.toDataURL('image/jpeg', 0.7);
        
        // Liberar recursos
        URL.revokeObjectURL(videoURL);
        
        // Resolver la promesa con la URL de la miniatura
        resolve({
          videoURL: videoURL,
          thumbnailURL: thumbnailURL
        });
      };
      
      // Manejar errores
      video.onerror = () => {
        console.error('Error loading video for thumbnail generation');
        URL.revokeObjectURL(videoURL);
        resolve({
          videoURL: videoURL,
          thumbnailURL: null
        });
      };
    });
  };
  
  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      
      // Verificar que solo sean archivos de video
      const validFiles = selectedFiles.filter(file => file.type.startsWith('video/'));
      
      if (validFiles.length !== selectedFiles.length) {
        setError('Solo se permiten archivos de video');
        return;
      }
      
      setFiles(validFiles);
      setLoading(true); // Mostrar indicador de carga mientras se generan las miniaturas
      
      try {
        // Generar miniaturas para cada video
        const previewPromises = validFiles.map(file => generateThumbnail(file));
        const previewResults = await Promise.all(previewPromises);
        
        // Guardar las URLs de los videos y las miniaturas
        setPreviews(previewResults);
      } catch (err) {
        console.error('Error generating thumbnails:', err);
        // Crear previews simples sin miniaturas en caso de error
        const fallbackPreviews = validFiles.map(file => ({
          videoURL: URL.createObjectURL(file),
          thumbnailURL: null
        }));
        setPreviews(fallbackPreviews);
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (files.length === 0) {
      setError('Por favor selecciona al menos un video para subir');
      return;
    }
    
    if (!user) {
      setError('Debes iniciar sesión para subir videos');
      return;
    }
    
    if (!selectedProfile) {
      setError('Por favor selecciona una colección');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setUploadProgress(0);
      
      // Los videos van de a uno: son grandes y subirlos en paralelo solo
      // compite por el mismo ancho de banda. Cada uno viaja por streaming, asi
      // que el tamaño ya no es un problema para el servidor.
      const totalFiles = files.length;
      let successfulUploads = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const preview = previews[i];

        // La miniatura se genera en el navegador como data URL; hay que
        // convertirla a Blob para poder subirla como archivo.
        let miniatura = null;
        if (preview?.thumbnailURL) {
          const blob = await fetch(preview.thumbnailURL).then((r) => r.blob());
          miniatura = new File([blob], `thumbnail-${file.name.split('.')[0]}.jpg`, {
            type: blob.type || 'image/jpeg',
          });
        }

        try {
          await uploadVideo(file, selectedProfile, {
            thumbnail: miniatura,
            onProgress: (evento) => {
              if (!evento.total) return;
              const progresoArchivo = (evento.loaded * 100) / evento.total;
              const general = (i * 100 + progresoArchivo) / totalFiles;
              setUploadProgress(Math.min(Math.round(general), 99));
            },
          });

          successfulUploads++;
        } catch (fileError) {
          console.error(`Error subiendo el video ${file.name}:`, fileError);
          // Seguimos con el resto: que uno falle no cancela la tanda.
        }

        setUploadProgress(Math.min(Math.round(((i + 1) / totalFiles) * 100), 99));
      }

      // Verificar resultado final
      if (successfulUploads > 0) {
        setSuccess(true);
        setUploadProgress(100);
        console.log(`Subida completada: ${successfulUploads} de ${totalFiles} videos`);
        
        // Redirigir al perfil después de 2 segundos
        setTimeout(() => {
          navigate(`/profile/${selectedProfile}`);
        }, 2000);
      } else {
        throw new Error(`No se pudo subir ningún video de ${totalFiles}`);
      }
      
    } catch (error) {
      console.error('Error uploading videos:', error);
      setError(error.message || 'Error uploading videos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleProfileChange = (e) => {
    setSelectedProfile(e.target.value);
  };
  
  const handleBackClick = () => {
    if (selectedProfile) {
      navigate(`/profile/${selectedProfile}`);
    } else {
      navigate('/');
    }
  };
  
  // Limpiar las URLs de objeto cuando el componente se desmonte
  useEffect(() => {
    return () => {
      // Ya no necesitamos limpiar las URLs de previews porque ahora creamos
      // y liberamos las URLs de objetos solo cuando se reproducen los videos
      // y las liberamos inmediatamente después de cargar los datos
    };
  }, []);
  
  return (
    <Container maxWidth="md" sx={{ pt: 10, pb: 6 }}>
      <UploadContainer elevation={3}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Subir Videos
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Videos subidos exitosamente
            </Alert>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleBackClick}
            >
              Volver a la colección
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="profile-select-label">Colección</InputLabel>
              <Select
                labelId="profile-select-label"
                id="profile-select"
                value={selectedProfile}
                label="Colección"
                onChange={handleProfileChange}
                disabled={loading}
              >
                <MenuItem value="" disabled>
                  Selecciona una colección
                </MenuItem>
                {profiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <label htmlFor="video-upload">
                <FileInput
                  accept="video/*"
                  id="video-upload"
                  multiple
                  type="file"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <UploadButton
                  variant="outlined"
                  component="span"
                  startIcon={<VideoLibraryIcon />}
                  disabled={loading}
                >
                  Seleccionar Videos
                </UploadButton>
              </label>
              
              <Typography variant="body2" color="textSecondary">
                Formatos aceptados: MP4, WebM, MOV, AVI
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Tamaño máximo: 100MB por video
              </Typography>
            </Box>
            
            {previews.length > 0 && (
              <PreviewContainer>
                {previews.map((preview, index) => (
                  <Box key={index} sx={{ textAlign: 'center', position: 'relative' }}>
                    {/* Mostrar la miniatura como fondo */}
                    <Box 
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%', // Relación de aspecto 16:9
                        backgroundColor: '#000',
                        backgroundImage: preview.thumbnailURL ? `url(${preview.thumbnailURL})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        // En lugar de crear un elemento de video dinámicamente,
                        // simplemente mostrar un reproductor de video estático
                        const container = document.getElementById(`video-preview-${index}`);
                        
                        // Limpiar el contenedor
                        container.innerHTML = '';
                        
                        // Crear un elemento de video
                        const videoElement = document.createElement('video');
                        videoElement.controls = true;
                        videoElement.style.width = '100%';
                        videoElement.style.height = '100%';
                        videoElement.style.position = 'absolute';
                        videoElement.style.top = '0';
                        videoElement.style.left = '0';
                        
                        // Configurar el video para usar el archivo directamente
                        // en lugar de la URL del blob
                        if (files[index]) {
                          const fileURL = URL.createObjectURL(files[index]);
                          videoElement.src = fileURL;
                          
                          // Liberar la URL cuando el video se descargue
                          videoElement.onloadeddata = () => {
                            URL.revokeObjectURL(fileURL);
                          };
                        }
                        
                        // Añadir el video al contenedor
                        container.appendChild(videoElement);
                        videoElement.play();
                      }}
                      id={`video-preview-${index}`}
                    >
                      {/* Icono de reproducir en el centro */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: 'white',
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          borderRadius: '50%',
                          padding: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <PlayArrowIcon fontSize="large" />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {files[index]?.name}
                    </Typography>
                  </Box>
                ))}
              </PreviewContainer>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBackClick}
                disabled={loading}
              >
                Cancelar
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || files.length === 0}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {loading ? 'Subiendo...' : 'Subir Videos'}
              </Button>
            </Box>
            
            {loading && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  {uploadProgress}% Completado
                </Typography>
              </Box>
            )}
          </form>
        )}
      </UploadContainer>
    </Container>
  );
};

export default UploadVideo;
